/**
 * A controlled tooltip component that works with both Link and button elements.
 * When href is provided, it renders as a Next.js Link.
 * When href is not provided, it renders as a button.
 */
import Link from "next/link";
import { Tooltip } from "@zapier/design-system-beta";
import { useEffect, useState, ReactNode } from "react";

type BaseProps = {
  key?: string;
  content: string;
  ariaLabel?: string;
  placement?: "top" | "bottom" | "left" | "right";
  offset?: "small" | "medium" | "large";
  className?: string;
  children: ReactNode;
};

type LinkProps = BaseProps & {
  href: string;
  target: string;
  onClick?: never;
};

type ButtonProps = BaseProps & {
  href?: never;
  target?: never;
  onClick: () => void;
};

type Props = LinkProps | ButtonProps;

export function ControlledTooltip({
  key,
  content,
  ariaLabel,
  placement = "top",
  offset = "large",
  className,
  onClick,
  href,
  target,
  children,
}: Props) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipTimeoutId, setTooltipTimeoutId] =
    useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutId) {
        clearTimeout(tooltipTimeoutId);
      }
    };
  }, [tooltipTimeoutId]);

  const handleFocus = () => {
    setIsTooltipVisible(true);
    if (tooltipTimeoutId) {
      clearTimeout(tooltipTimeoutId);
    }
    setTooltipTimeoutId(setTimeout(() => setIsTooltipVisible(false), 1500)); // 2000ms = 2 seconds
  };

  const handleBlur = () => {
    if (tooltipTimeoutId) {
      clearTimeout(tooltipTimeoutId);
    }
    setIsTooltipVisible(false);
  };

  const handleMouseEnter = () => {
    setIsTooltipVisible(true);
    if (tooltipTimeoutId) {
      clearTimeout(tooltipTimeoutId);
    }
    setTooltipTimeoutId(setTimeout(() => setIsTooltipVisible(false), 1500)); // 1.5 seconds
  };

  const handleMouseLeave = () => {
    if (tooltipTimeoutId) {
      clearTimeout(tooltipTimeoutId);
    }
    setIsTooltipVisible(false);
  };

  const getTag = (href: Props["href"]) => {
    if (href) {
      return Link;
    }
    return "button";
  };

  const Component = getTag(href) as typeof Link | "button";

  return (
    <Tooltip
      key={key}
      content={content}
      isOpen={isTooltipVisible}
      placement={placement}
      offset={offset}
    >
      <Tooltip.FocusableTrigger>
        {/* @ts-ignore - Component is either Link or button based on href prop */}
        <Component
          aria-label={ariaLabel}
          className={className}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={onClick}
          {...(href ? { href, target } : {})}
        >
          {children}
        </Component>
      </Tooltip.FocusableTrigger>
    </Tooltip>
  );
}
